import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { styled } from "@mui/material";
import Chat from "messaging/entities/forum/view/components/chat";
import Guid from "common/values/guid/guid";
import MessagingAPIService from "messaging/api/messaging-api-service";
import Forum from "messaging/entities/forum/forum";
import ForumSelection from "./forum-selection";
import Loader from "common/components/loader";
import { useSession } from "users/session/session-context";
import QueryForumsApiRequest, { ForumFilterParameters, ForumOrderParameters } from "messaging/entities/forum/api/request-contracts/query-forums-api-request";
import PaginationParameters from "common/contracts/pagination-parameters";
import { CanceledError } from "axios";

const ForumContainer = styled("div")(
  ({ theme }) => ({
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    paddingTop: theme.spacing(1),
    width: '100%'
  }));
const SelectionContainer = styled("div")(
  () => ({
    display: 'flex',
    gridRowStart: 1
  }));
const ChatContainer = styled("div")(
  () => ({
    gridRowStart: 2,
    gridRowEnd: 'none'
  }));
const LoaderContainer = styled("div")(
  ({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(4),
    paddingTop: 0,
    width: '100%'
  }));

type ChatDialogProps = {
  forumId?: Guid;
  forums?: Forum[];
  getAllForums?: boolean;
  loadedForum?: Forum;
  onLeaveConversation?: () => void;
};

export default function ChatDialog(props: Readonly<ChatDialogProps>) {
  const {
    forumId,
    forums,
    getAllForums,
    loadedForum,
    onLeaveConversation
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allForums, setAllForums] = useState<Forum[]>([]);
  const [individualForums, setIndividualForums] = useState<Forum[]>([]);
  const [teamForums, setTeamForums] = useState<Forum[]>([]);
  const [proposalForums, setProposalForums] = useState<Forum[]>([]);
  const [workForums, setWorkForums] = useState<Forum[]>([]);
  const [selectedForum, setSelectedForum] = useState<Forum | undefined>();
  const session = useSession();

  const loadChatDialog = async (abortController: AbortController) => {
    try {
      setIsLoading(true);

      if (loadedForum) {
        setSelectedForum(loadedForum);
        return;
      }

      const filter = new ForumFilterParameters();
      filter.forumId = forumId?.toString();
      const order = new ForumOrderParameters();
      const pagination = new PaginationParameters(0, 25);
      const request = new QueryForumsApiRequest(
        filter,
        order,
        pagination
      );
      const messagingService = new MessagingAPIService(session);
      const forums = await messagingService.getForums(request, abortController);

      if (forumId) {
        if (!forums[0].id?.isEqualTo(forumId)) throw new Error("Forum not found");
        setSelectedForum(forums[0]);
        return;
      }

      setAllForums(forums);
      setIndividualForums(
        forums.filter(
          (forum) =>
            forum.topic?.entityClass &&
            [
              "UserNetworkConnection",
              "Marketplace.Individual"
            ].includes(
              forum.topic.entityClass
            )
        )
      );
      setTeamForums(
        forums.filter(
          (forum) =>
            forum.topic?.entityClass &&
            ["Marketplace.Team"].includes(forum.topic.entityClass)
        )
      );
      setProposalForums(
        forums.filter(
          (forum) =>
            forum.topic?.entityClass &&
            ["Work.Proposal"].includes(forum.topic.entityClass)
        )
      );
      setWorkForums(
        forums.filter(
          (forum) =>
            forum.topic?.entityClass &&
            ["Work"].includes(forum.topic.entityClass)
        )
      );
    } catch (err) {
      if (err instanceof CanceledError) return;
      console.error(err);

      enqueueSnackbar(
        "Failed to get contacts",
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let abortController = new AbortController();
    void loadChatDialog(abortController);

    return () => {
      abortController.abort();
      abortController = new AbortController();
    }
  }, []);

  return (
    <>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!isLoading && (
        <ForumContainer>
          {(!forums && !getAllForums && !selectedForum) && (
            <SelectionContainer>
              <ForumSelection
                individualForums={individualForums}
                teamForums={teamForums}
                proposalForums={proposalForums}
                workForums={workForums}
                onForumSelected={setSelectedForum}
              />
            </SelectionContainer>
          )}
          {(!forums && getAllForums) && (
            <ChatContainer>
              <Chat
                forums={allForums}
                onLeaveConversation={onLeaveConversation}
              />
            </ChatContainer>
          )}
          {selectedForum && (
            <ChatContainer>
              <Chat
                forums={[selectedForum]}
                onLeaveConversation={onLeaveConversation}
              />
            </ChatContainer>
          )}
          {forums && (
            <ChatContainer>
              <Chat
                forums={forums}
                onLeaveConversation={onLeaveConversation}
              />
            </ChatContainer>
          )}
        </ForumContainer>
      )}
    </>
  );
}
